import { useState } from "react";
import { GitBranch, Plus, Settings, ArrowRightLeft, Star, Check, Instagram, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import type { SalesFunnel } from "@/types/lead";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";

function FunnelChannelIcon({ name }: { name: string }) {
  const lower = name.toLowerCase();
  if (lower.startsWith("whatsapp")) return <MessageCircle className="w-3 h-3 text-green-500" />;
  if (lower.startsWith("instagram")) return <Instagram className="w-3 h-3 text-pink-500" />;
  if (lower.startsWith("messenger")) return <MessengerIcon className="text-blue-500" size={12} />;
  return null;
}

interface FunnelDropdownMenuProps {
  funnels: SalesFunnel[];
  activeFunnelId: string | null;
  onSelectFunnel: (id: string | null) => void;
  /** Ex.: Negociações — começa em “todos” para não esconder dados legados. */
  includeAllFunnelsOption?: boolean;
  allFunnelsLabel?: string;
  onOpenManager: () => void;
  onOpenMoveLeads: () => void;
  loading?: boolean;
  integrationById?: WorkspaceIntegrationsMap;
  /** Quando false, só troca de funil (ex.: página de Negociações). */
  showManagementActions?: boolean;
}

export function FunnelDropdownMenu({
  funnels,
  activeFunnelId,
  onSelectFunnel,
  onOpenManager,
  onOpenMoveLeads,
  loading = false,
  integrationById,
  showManagementActions = true,
  includeAllFunnelsOption = false,
  allFunnelsLabel = "Todos os funis",
}: FunnelDropdownMenuProps) {
  const [open, setOpen] = useState(false);
  
  const activeFunnel = funnels.find((f) => f.id === activeFunnelId);

  const handleSelectFunnel = (id: string | null) => {
    onSelectFunnel(id);
    setOpen(false);
  };

  const handleOpenManager = () => {
    setOpen(false);
    onOpenManager();
  };

  const handleOpenMoveLeads = () => {
    setOpen(false);
    onOpenMoveLeads();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-w-0 max-w-full shrink"
          disabled={loading}
        >
          <GitBranch className="w-4 h-4 shrink-0 sm:mr-2" />
          <span className="hidden sm:inline min-w-0 truncate">
            {loading
              ? "Carregando..."
              : activeFunnel
                ? resolveFunnelDisplayName(activeFunnel, integrationById)
                : includeAllFunnelsOption
                  ? allFunnelsLabel
                  : "Funis"}
          </span>
          {activeFunnel && (
            <div className={cn("w-2 h-2 rounded-full ml-2 hidden sm:block", activeFunnel.color)} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-popover border shadow-lg z-50">
        <DropdownMenuLabel>Selecionar Funil</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {includeAllFunnelsOption && funnels.length > 0 && (
          <DropdownMenuItem
            onClick={() => handleSelectFunnel(null)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <GitBranch className="w-3 h-3 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate">{allFunnelsLabel}</span>
            {activeFunnelId === null && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        )}

        {includeAllFunnelsOption && funnels.length > 0 && <DropdownMenuSeparator />}

        {funnels.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            Nenhum funil cadastrado.
            <br />
            Clique em "Gerenciar Funis" para criar.
          </div>
        ) : (
          funnels.map((funnel) => (
            <DropdownMenuItem
              key={funnel.id}
              onClick={() => handleSelectFunnel(funnel.id)}
              className="flex items-center gap-3 cursor-pointer"
            >
              <div className={cn("w-3 h-3 rounded-full shrink-0", funnel.color)} />
              {funnel.integration_id && <FunnelChannelIcon name={funnel.name} />}
              <span className="flex-1 truncate">
                {resolveFunnelDisplayName(funnel, integrationById)}
              </span>
              {funnel.is_default && (
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
              )}
              {funnel.id === activeFunnelId && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))
        )}
        
        {showManagementActions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleOpenManager} className="cursor-pointer">
              <Plus className="w-4 h-4 mr-2" />
              Criar / Gerenciar Funis
            </DropdownMenuItem>
            {funnels.length > 1 && (
              <DropdownMenuItem onClick={handleOpenMoveLeads} className="cursor-pointer">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Mover Leads Entre Funis
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
